/**
 * Single sign-on through Keycloak (CSI-344).
 *
 * The app never talks to Google or Microsoft. It starts an OpenID Connect
 * authorization code flow (with PKCE: a public client keeps no secret) against
 * the realm `silia` and gets back the realm's own tokens: the same `accountId`,
 * `allowedAccountIds` and roles the legacy token carries, which the API accepts
 * next to it (shared/clases/aws/KeycloakTokenVerifier.class.ts).
 *
 * Three ways in, one flow:
 *   - «Continue with Google» / «Continue with Microsoft»: `kc_idp_hint=<provider>`
 *     sends the browser straight to that provider, past Keycloak's own page.
 *   - «Log in with SSO»: no hint; Keycloak shows its login page, where the
 *     realm's identity providers (a customer's Entra ID or Okta, later) live.
 *
 * Only users that already exist in the realm can sign in this way (the realm's
 * first-broker-login flow refuses unknown e-mails); nothing here creates users.
 *
 * No `config/featureFlags` import here: `utils/httpClient` (bundled into the
 * embeddable widget, whose build mocks the config module) imports this file
 * for the session refresh. The flag lives in ssoAvailability.ts.
 *
 * Storage: the access token goes under the same key the legacy login uses, so
 * every API call and the session restore work unchanged; `authProvider` says
 * which login issued it, because a Keycloak session refreshes at Keycloak's
 * token endpoint and ends at its end_session.
 */
import config from '../../../config';
import {
  AUTH_PROVIDERS,
  AUTH_PROVIDER_STORAGE_KEY,
  AUTH_STORAGE_KEY,
  ID_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
  SSO_CALLBACK_PATH,
} from '../constants';

/** The social providers the login page can offer a button for (Keycloak aliases). */
export const SSO_PROVIDERS = ['google', 'microsoft'] as const;
export type SsoProvider = (typeof SSO_PROVIDERS)[number];

const PKCE_STORAGE_KEY = 'silia.sso.pkce';

export interface KeycloakTokens {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
}

export type SsoErrorCode = 'denied' | 'state' | 'missingCode' | 'exchange';

/** A failed sign-in, by cause; the login page shows one generic message for all. */
export class SsoError extends Error {
  constructor(
    public readonly code: SsoErrorCode,
    message?: string,
  ) {
    super(message || `SSO ${code}`);
    this.name = 'SsoError';
  }
}

interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

function keycloak(): KeycloakConfig {
  const kc = (config as { keycloak?: Partial<KeycloakConfig> }).keycloak || {};
  return {
    url: (kc.url || '').replace(/\/+$/, ''),
    realm: kc.realm || 'silia',
    clientId: kc.clientId || 'silia-frontend',
  };
}

function endpoint(name: 'auth' | 'token' | 'logout'): string {
  const { url, realm } = keycloak();
  return `${url}/realms/${realm}/protocol/openid-connect/${name}`;
}

export function ssoCallbackUri(): string {
  return `${window.location.origin}${SSO_CALLBACK_PATH}`;
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64Url(buffer);
}

async function s256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Builds the authorization URL and remembers the PKCE verifier and the state
 * for the callback (sessionStorage: this tab, this sign-in). With a provider,
 * `kc_idp_hint` skips Keycloak's page; without one, Keycloak shows it.
 */
export async function buildSsoLoginUrl(provider?: SsoProvider): Promise<string> {
  const verifier = randomString(48);
  const state = randomString(24);
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify({ verifier, state }));
  const params = new URLSearchParams({
    client_id: keycloak().clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: ssoCallbackUri(),
    state,
    code_challenge: await s256(verifier),
    code_challenge_method: 'S256',
  });
  if (provider) params.set('kc_idp_hint', provider);
  return `${endpoint('auth')}?${params.toString()}`;
}

/** Sends the browser to Keycloak (and on to the provider). Never resolves to a page of ours. */
export async function startSsoLogin(provider?: SsoProvider): Promise<void> {
  window.location.assign(await buildSsoLoginUrl(provider));
}

async function tokenRequest(fields: Record<string, string>): Promise<KeycloakTokens> {
  const body = new URLSearchParams({ client_id: keycloak().clientId, ...fields });
  const response = await fetch(endpoint('token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) throw new SsoError('exchange', `HTTP ${response.status}`);
  const data = (await response.json()) as {
    access_token?: string;
    refresh_token?: string;
    id_token?: string;
  };
  if (!data.access_token || !data.refresh_token)
    throw new SsoError('exchange', 'No tokens in the response');
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    idToken: data.id_token,
  };
}

/**
 * Finishes the sign-in on /auth/callback: checks the state, exchanges the code
 * (with the PKCE verifier) for the realm's tokens. The stored PKCE data is
 * consumed whatever happens, so a code cannot be exchanged twice.
 */
export async function completeSsoLogin(search: string): Promise<KeycloakTokens> {
  const params = new URLSearchParams(search);
  const stored = sessionStorage.getItem(PKCE_STORAGE_KEY);
  sessionStorage.removeItem(PKCE_STORAGE_KEY);

  const error = params.get('error');
  if (error) throw new SsoError('denied', params.get('error_description') || error);
  const code = params.get('code');
  if (!code) throw new SsoError('missingCode');

  let pkce: { verifier?: string; state?: string } | null = null;
  try {
    pkce = stored ? JSON.parse(stored) : null;
  } catch {
    pkce = null;
  }
  if (!pkce?.verifier || !pkce.state || pkce.state !== params.get('state'))
    throw new SsoError('state');

  return tokenRequest({
    grant_type: 'authorization_code',
    code,
    redirect_uri: ssoCallbackUri(),
    code_verifier: pkce.verifier,
  });
}

/** Rotates the pair at Keycloak's token endpoint (each refresh token is single-use). */
export async function refreshKeycloakSession(refreshToken: string): Promise<KeycloakTokens> {
  return tokenRequest({ grant_type: 'refresh_token', refresh_token: refreshToken });
}

export function storeKeycloakSession(tokens: KeycloakTokens): void {
  localStorage.setItem(AUTH_STORAGE_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, tokens.refreshToken);
  localStorage.setItem(AUTH_PROVIDER_STORAGE_KEY, AUTH_PROVIDERS.KEYCLOAK);
  if (tokens.idToken) localStorage.setItem(ID_TOKEN_STORAGE_KEY, tokens.idToken);
}

export function isKeycloakSession(): boolean {
  try {
    return localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY) === AUTH_PROVIDERS.KEYCLOAK;
  } catch {
    return false;
  }
}

export function clearStoredSession(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  localStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY);
  localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
}

/**
 * Keycloak's end_session URL. With the id_token_hint Keycloak ends the session
 * without asking and returns to the app's login; the realm allows the same
 * post-logout URLs as redirect URLs.
 */
export function keycloakLogoutUrl(
  postLogoutRedirectUri = `${window.location.origin}/login`,
): string {
  const params = new URLSearchParams({
    client_id: keycloak().clientId,
    post_logout_redirect_uri: postLogoutRedirectUri,
  });
  const idToken = localStorage.getItem(ID_TOKEN_STORAGE_KEY);
  if (idToken) params.set('id_token_hint', idToken);
  return `${endpoint('logout')}?${params.toString()}`;
}
